/**
 * One reading of "how is this receiver doing?", shared by every surface that shows it.
 *
 * The backend already resolves three states (ReceiverStatusResolver) and broadcasts them on
 * every receiverStatistics tick. They are not "online/offline" — the middle one is the whole
 * point:
 *
 *   ACTIVE    socket connected AND a parsed message within the last 60 s  -> good
 *   CONNECTED socket connected but nothing arriving (just opened, or stalled) -> ailing
 *   OFFLINE   no socket at all                                            -> dead
 *
 * A receiver that is CONNECTED but silent looks online to a naive `isActive` check while
 * feeding us nothing, which is exactly the failure worth seeing on the live map.
 */

export const ACTIVE = 'active';
export const STALLED = 'stalled';
export const OFFLINE = 'offline';

/**
 * State for one receiver record.
 *
 * Reads `status` first and only falls back to the older boolean `isActive` — a record that
 * predates the three-state broadcast can only express two of them, and guessing STALLED from a
 * boolean would invent a state the server never reported.
 */
export function receiverState(receiver) {
  if (!receiver) return OFFLINE;
  const raw = typeof receiver.status === 'string' ? receiver.status.toUpperCase() : null;
  if (raw === 'ACTIVE') return ACTIVE;
  if (raw === 'CONNECTED') return STALLED;
  if (raw === 'OFFLINE') return OFFLINE;
  return receiver.isActive ? ACTIVE : OFFLINE;
}

/**
 * State for a set of receivers, for the "All Receivers" selection.
 *
 * Worst-case-aware rather than optimistic: green only when every receiver is feeding us, amber
 * while at least one is, red when none is. Showing green because one of four still works would
 * hide exactly the outage the indicator exists to report.
 */
export function aggregateState(receivers) {
  const list = (receivers || []).filter(Boolean);
  if (list.length === 0) return OFFLINE;
  const states = list.map(receiverState);
  if (states.every(s => s === ACTIVE)) return ACTIVE;
  if (states.some(s => s === ACTIVE)) return STALLED;
  return OFFLINE;
}

/** Plain-English label, for tooltips and for themes that carry no hue. */
export function stateLabel(state) {
  if (state === ACTIVE) return 'receiving';
  if (state === STALLED) return 'connected, no data';
  return 'offline';
}

/** Counts per state, for a tooltip that explains an aggregate reading. */
export function stateSummary(receivers) {
  const counts = { [ACTIVE]: 0, [STALLED]: 0, [OFFLINE]: 0 };
  for (const r of receivers || []) counts[receiverState(r)] += 1;
  const parts = [];
  if (counts[ACTIVE]) parts.push(`${counts[ACTIVE]} receiving`);
  if (counts[STALLED]) parts.push(`${counts[STALLED]} connected, no data`);
  if (counts[OFFLINE]) parts.push(`${counts[OFFLINE]} offline`);
  return parts.join(' · ');
}
